import { useEffect, useState } from "react";

const STORAGE_PREFIX = "aba-progress-ch-";
const META_KEY = "aba-progress-meta";

// Hook for the optional Cloudflare KV sync layer. Set by sync.ts after the
// app boots so progress mutations also trigger a debounced remote push.
type RemotePush = (payload: {
  meta: ProgressMeta;
  chapters: Record<string, ChapterProgress>;
  examHistory?: unknown[];
}) => void;
let remotePush: RemotePush | undefined;

export function setRemotePush(fn: RemotePush | undefined) {
  remotePush = fn;
}

export type FlashcardRating = "again" | "hard" | "got_it";

export interface ScheduledCard {
  reps: number;
  ease: number;
  interval: number;
  due: string;
  lastReviewedAt: string;
  lapses: number;
}

export interface ChapterProgress {
  quizBestScore: number;
  quizTotal: number;
  quizAttempts: number;
  quizAnswers: Record<string, string>;
  /** Per-question history: how many times attempted and last outcome. */
  quizQuestionAttempts: Record<string, { attempts: number; lastCorrect: boolean }>;
  /** Last N quiz attempt summaries for trend lines. */
  quizAttemptHistory: { score: number; total: number; at: string }[];
  checklistTicked: number[];
  flashcardSeen: string[];
  flashcardPos: number;
  flashcardMastery: Record<string, FlashcardRating>;
  /** Per-term SM-2 scheduler state, keyed by flashcard term. */
  flashcardSchedule: Record<string, ScheduledCard>;
  flashcardSessionsCompleted: number;
  /** Total active time spent on this chapter, in milliseconds. */
  timeSpentMs: number;
  /** Active time per section id (overview, glossary, ...) in milliseconds. */
  timeBySection: Record<string, number>;
  /** Highest scroll progress reached, 0-100. */
  maxScrollDepthPct: number;
  /** Number of distinct in-app sessions on this chapter. */
  sessionCount: number;
  /** First time the user opened this chapter. */
  firstVisited?: string;
  lastVisited?: string;
  completedAt?: string;
}

export interface AiPracticeAttempt {
  topic: string;
  mode: "definitional" | "scenario";
  chapterId?: string;
  correct: number;
  total: number;
  at: string;
}

export interface ProgressMeta {
  studiedChapters: string[];
  firstVisit?: string;
  lastActiveAt?: string;
  /** Consecutive UTC days with any activity. */
  streakDays: number;
  /** Longest streak ever reached. */
  longestStreakDays: number;
  /** YYYY-MM-DD of the day the current streak last incremented. */
  lastStreakDate?: string;
  /** Map of YYYY-MM-DD -> active milliseconds that day. */
  studyDaysMap: Record<string, number>;
  /** Cross-chapter quick-drill sessions completed. */
  drillSessionsCompleted: number;
  /** Total active milliseconds across all chapters. */
  totalStudyMs: number;
  /** 25-minute Pomodoro focus sessions completed. */
  focusSessionsCompleted: number;
  /** Total minutes accumulated inside Focus sessions. */
  focusMinutesTotal: number;
  /** Last AI practice attempts (most recent 50). */
  aiPracticeAttempts: AiPracticeAttempt[];
}

export const EMPTY_PROGRESS: ChapterProgress = {
  quizBestScore: 0,
  quizTotal: 0,
  quizAttempts: 0,
  quizAnswers: {},
  quizQuestionAttempts: {},
  quizAttemptHistory: [],
  checklistTicked: [],
  flashcardSeen: [],
  flashcardPos: 0,
  flashcardMastery: {},
  flashcardSchedule: {},
  flashcardSessionsCompleted: 0,
  timeSpentMs: 0,
  timeBySection: {},
  maxScrollDepthPct: 0,
  sessionCount: 0,
};

export const EMPTY_META: ProgressMeta = {
  studiedChapters: [],
  streakDays: 0,
  longestStreakDays: 0,
  studyDaysMap: {},
  drillSessionsCompleted: 0,
  totalStudyMs: 0,
  focusSessionsCompleted: 0,
  focusMinutesTotal: 0,
  aiPracticeAttempts: [],
};

function cloneDefault<T>(value: T): T {
  if (value === null || typeof value !== "object") return value;
  if (typeof structuredClone === "function") return structuredClone(value);
  return JSON.parse(JSON.stringify(value)) as T;
}

function readJSON<T>(key: string, fallback: T): T {
  const safeFallback = cloneDefault(fallback);
  if (typeof window === "undefined") return safeFallback;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return safeFallback;
    return { ...safeFallback, ...JSON.parse(raw) } as T;
  } catch {
    return safeFallback;
  }
}

function writeJSON(key: string, value: unknown) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
    window.dispatchEvent(new CustomEvent("aba-progress-change", { detail: { key } }));
    schedulePushSnapshot();
  } catch {
    // storage quota or private mode; silently drop
  }
}

function schedulePushSnapshot() {
  if (!remotePush) return;
  const meta = loadMeta();
  const chapters = loadAllProgress();
  // Include the aba-exam-history blob (Mock Exam Result[]) when present so
  // the server stores the full cross-device exam record. Empty array
  // emitted otherwise so the schema is stable.
  let examHistory: unknown[] = [];
  if (typeof window !== "undefined") {
    try {
      const raw = window.localStorage.getItem("aba-exam-history");
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) examHistory = parsed;
      }
    } catch {
      // ignore
    }
  }
  remotePush({ meta, chapters, examHistory });
}

export function loadChapterProgress(chapterId: string): ChapterProgress {
  return readJSON<ChapterProgress>(STORAGE_PREFIX + chapterId, EMPTY_PROGRESS);
}

export function saveChapterProgress(chapterId: string, value: ChapterProgress) {
  writeJSON(STORAGE_PREFIX + chapterId, value);
  const meta = readJSON<ProgressMeta>(META_KEY, EMPTY_META);
  const now = new Date();
  const nowIso = now.toISOString();
  const ymd = nowIso.slice(0, 10);
  if (!meta.firstVisit) meta.firstVisit = nowIso;
  meta.lastActiveAt = nowIso;
  if (!meta.studiedChapters.includes(chapterId)) {
    meta.studiedChapters = [...meta.studiedChapters, chapterId];
  }
  // Streak tracking: any save counts as activity for today.
  if (meta.lastStreakDate !== ymd) {
    const last = meta.lastStreakDate;
    if (last) {
      const lastDate = new Date(`${last}T00:00:00Z`);
      const today = new Date(`${ymd}T00:00:00Z`);
      const diffDays = Math.round((today.getTime() - lastDate.getTime()) / 86400000);
      meta.streakDays = diffDays === 1 ? (meta.streakDays ?? 0) + 1 : 1;
    } else {
      meta.streakDays = 1;
    }
    meta.lastStreakDate = ymd;
    if ((meta.streakDays ?? 0) > (meta.longestStreakDays ?? 0)) {
      meta.longestStreakDays = meta.streakDays;
    }
  }
  // Recompute totalStudyMs from the snapshot of all chapters every save. Cheap
  // since we read chapter blobs in one pass.
  meta.totalStudyMs = sumAllTimeSpentMs();
  writeJSON(META_KEY, meta);
}

function sumAllTimeSpentMs(): number {
  if (typeof window === "undefined") return 0;
  let total = 0;
  for (let i = 0; i < window.localStorage.length; i++) {
    const k = window.localStorage.key(i);
    if (!k || !k.startsWith(STORAGE_PREFIX)) continue;
    const raw = window.localStorage.getItem(k);
    if (!raw) continue;
    try {
      const p = JSON.parse(raw) as ChapterProgress;
      total += p.timeSpentMs ?? 0;
    } catch {
      // ignore
    }
  }
  return total;
}

export function bumpStudyMinutes(ms: number) {
  if (ms <= 0) return;
  const meta = readJSON<ProgressMeta>(META_KEY, EMPTY_META);
  const ymd = new Date().toISOString().slice(0, 10);
  meta.studyDaysMap = { ...meta.studyDaysMap, [ymd]: (meta.studyDaysMap[ymd] ?? 0) + ms };
  meta.totalStudyMs = (meta.totalStudyMs ?? 0) + ms;
  writeJSON(META_KEY, meta);
}

export function bumpDrillSession() {
  const meta = readJSON<ProgressMeta>(META_KEY, EMPTY_META);
  meta.drillSessionsCompleted = (meta.drillSessionsCompleted ?? 0) + 1;
  writeJSON(META_KEY, meta);
}

export function bumpFocusSession(minutes: number) {
  const meta = readJSON<ProgressMeta>(META_KEY, EMPTY_META);
  meta.focusSessionsCompleted = (meta.focusSessionsCompleted ?? 0) + 1;
  meta.focusMinutesTotal = (meta.focusMinutesTotal ?? 0) + Math.max(0, Math.round(minutes));
  writeJSON(META_KEY, meta);
}

export function recordAiPracticeAttempt(attempt: Omit<AiPracticeAttempt, "at">) {
  const meta = readJSON<ProgressMeta>(META_KEY, EMPTY_META);
  const list = [
    ...(meta.aiPracticeAttempts ?? []),
    { ...attempt, at: new Date().toISOString() },
  ].slice(-50);
  meta.aiPracticeAttempts = list;
  writeJSON(META_KEY, meta);
}

export function loadMeta(): ProgressMeta {
  return readJSON<ProgressMeta>(META_KEY, EMPTY_META);
}

export function loadAllProgress(): Record<string, ChapterProgress> {
  if (typeof window === "undefined") return {};
  const out: Record<string, ChapterProgress> = {};
  for (let i = 0; i < window.localStorage.length; i++) {
    const k = window.localStorage.key(i);
    if (!k || !k.startsWith(STORAGE_PREFIX)) continue;
    const id = k.slice(STORAGE_PREFIX.length);
    out[id] = readJSON<ChapterProgress>(k, EMPTY_PROGRESS);
  }
  return out;
}

export function resetChapterProgress(chapterId: string) {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(STORAGE_PREFIX + chapterId);
  window.dispatchEvent(new CustomEvent("aba-progress-change", { detail: { key: STORAGE_PREFIX + chapterId } }));
}

export function resetAllProgress() {
  if (typeof window === "undefined") return;
  const keys: string[] = [];
  for (let i = 0; i < window.localStorage.length; i++) {
    const k = window.localStorage.key(i);
    if (k && (k.startsWith(STORAGE_PREFIX) || k === META_KEY)) keys.push(k);
  }
  for (const k of keys) window.localStorage.removeItem(k);
  window.dispatchEvent(new CustomEvent("aba-progress-change", { detail: { key: "*" } }));
  schedulePushSnapshot();
}

export function hydrateLocalFromRemote(payload: {
  meta: ProgressMeta;
  chapters: Record<string, ChapterProgress>;
  examHistory?: unknown[];
}) {
  if (typeof window === "undefined") return;
  // Wipe local chapter keys we are about to overwrite to avoid stale entries
  // not present in the remote payload.
  const toRemove: string[] = [];
  for (let i = 0; i < window.localStorage.length; i++) {
    const k = window.localStorage.key(i);
    if (k && k.startsWith(STORAGE_PREFIX) && !payload.chapters[k.slice(STORAGE_PREFIX.length)]) {
      toRemove.push(k);
    }
  }
  for (const k of toRemove) window.localStorage.removeItem(k);
  for (const [chapterId, prog] of Object.entries(payload.chapters)) {
    window.localStorage.setItem(STORAGE_PREFIX + chapterId, JSON.stringify(prog));
  }
  window.localStorage.setItem(META_KEY, JSON.stringify(payload.meta));
  if (Array.isArray(payload.examHistory)) {
    window.localStorage.setItem("aba-exam-history", JSON.stringify(payload.examHistory));
  }
  window.dispatchEvent(new CustomEvent("aba-progress-change", { detail: { key: "*" } }));
}

export function useChapterProgress(chapterId: string) {
  const [state, setState] = useState<ChapterProgress>(() => loadChapterProgress(chapterId));

  useEffect(() => {
    setState(loadChapterProgress(chapterId));
  }, [chapterId]);

  useEffect(() => {
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (!detail) return;
      if (detail.key === STORAGE_PREFIX + chapterId || detail.key === "*") {
        setState(loadChapterProgress(chapterId));
      }
    };
    window.addEventListener("aba-progress-change", onChange);
    return () => window.removeEventListener("aba-progress-change", onChange);
  }, [chapterId]);

  const update = (patch: Partial<ChapterProgress> | ((prev: ChapterProgress) => ChapterProgress)) => {
    setState(prev => {
      const next = typeof patch === "function" ? patch(prev) : { ...prev, ...patch };
      saveChapterProgress(chapterId, next);
      return next;
    });
  };

  return { state, update, reset: () => resetChapterProgress(chapterId) };
}

export function useAllProgress() {
  const [all, setAll] = useState<Record<string, ChapterProgress>>(() => loadAllProgress());
  const [meta, setMeta] = useState<ProgressMeta>(() => loadMeta());

  useEffect(() => {
    const reload = () => {
      setAll(loadAllProgress());
      setMeta(loadMeta());
    };
    window.addEventListener("aba-progress-change", reload);
    return () => window.removeEventListener("aba-progress-change", reload);
  }, []);

  return { all, meta };
}

export interface ChapterScoreSummary {
  attempted: boolean;
  scorePct?: number;
  checklistDone: number;
  checklistTotal: number;
  flashcardSeen: number;
  flashcardMastered: number;
  flashcardSessionsCompleted: number;
  timeSpentMs: number;
  visited: boolean;
}

export function summariseChapter(p: ChapterProgress | undefined, checklistTotal: number, _flashTotal: number): ChapterScoreSummary {
  const attempted = !!(p && p.quizAttempts > 0 && p.quizTotal > 0);
  const mastery = p?.flashcardMastery ?? {};
  const mastered = Object.values(mastery).filter(r => r === "got_it").length;
  return {
    attempted,
    scorePct: attempted && p ? Math.round((p.quizBestScore / Math.max(p.quizTotal, 1)) * 100) : undefined,
    checklistDone: p?.checklistTicked.length ?? 0,
    checklistTotal,
    flashcardSeen: p?.flashcardSeen.length ?? 0,
    flashcardMastered: mastered,
    flashcardSessionsCompleted: p?.flashcardSessionsCompleted ?? 0,
    timeSpentMs: p?.timeSpentMs ?? 0,
    visited: !!p?.lastVisited,
  };
}

export function formatDuration(ms: number): string {
  if (!ms || ms < 1000) return "0s";
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) {
    const rem = m % 60;
    return rem === 0 ? `${h}h` : `${h}h ${rem}m`;
  }
  const d = Math.floor(h / 24);
  const remH = h % 24;
  return remH === 0 ? `${d}d` : `${d}d ${remH}h`;
}
